
import ChatWindow from './ChatWindow.vue'
import { useFlowiseQuestionStream } from '@/use/use.flowiseQuestionStream'
import type { ChatMessage, ChatConfig } from '@/use/use.flowiseQuestionStream'
import { ref, computed, onMounted } from 'vue'
import { useUser } from '@/swr/swr.user'
import { useI18n } from 'vue-i18n'
import { FLOWISE_FLOW_ID } from '@/consts'
import { getCompanyDetails } from '@/api/api.portal'

export default {
  components: {
    ChatWindow,
  },
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
  },
  emits: ['close'],
  setup() {
    const { t } = useI18n()

    const { user } = useUser()
    const avatar = computed(
      () => user.value?.image || '/assets/avatarRabbit.svg'
    )
    const signedAiDisclaimer = ref(false)

    const chatConfig: ChatConfig = {
      flowId: FLOWISE_FLOW_ID,
      title: t('ai-chat.TITLE'),
      questionSuggestions: [
        t('ai-chat.QUESTION_SUGGESTION_1'),
        t('ai-chat.QUESTION_SUGGESTION_2'),
        t('ai-chat.QUESTION_SUGGESTION_3'),
        t('ai-chat.QUESTION_SUGGESTION_4'),
      ],
    }

    const messages = ref<Array<ChatMessage>>([])

    const { isLoading, askQuestion } = useFlowiseQuestionStream(
      chatConfig.flowId,
      user
    )

    const isEndFragment = (fragmentType: string) => {
      return (
        fragmentType === 'End' ||
        fragmentType === 'Response' ||
        fragmentType === 'Noop'
      )
    }

    const handleSubmit = async (question: string) => {
      // Append the question - and a response that we can update while streaming
      messages.value = [
        ...messages.value,
        { text: question, messageType: 'Question' },
        { text: '', messageType: 'Response' },
      ]

      await askQuestion(
        question,
        (chunk) => {
          // Append the incoming stream chunks of text to the current chat response
          const messagesCopy = [...messages.value]
          const indexOfLastMessage = messagesCopy.length - 1
          const lastMessageCopy = messagesCopy[indexOfLastMessage]

          if (!lastMessageCopy) return

          // If the current message is empty and we receive an end fragment, throw an error
          if (
            lastMessageCopy.text === '' &&
            isEndFragment(chunk.fragmentType)
          ) {
            throw new Error('Empty response received from Flowise')
          }

          // Otherwise, ignore end, noop and response fragments
          if (isEndFragment(chunk.fragmentType)) {
            return
          }

          messagesCopy.splice(indexOfLastMessage, 1, {
            messageType: 'Response',
            text: lastMessageCopy.text + chunk.text,
          })

          messages.value = messagesCopy
        },
        (errorMessage) => {
          // Append error message as a special (error) chat response
          const messagesCopy = [...messages.value]
          const indexOfLastMessage = messagesCopy.length - 1
          const lastMessageCopy = messagesCopy[indexOfLastMessage]

          messagesCopy.splice(indexOfLastMessage, 1, {
            messageType: 'Error',
            text: lastMessageCopy.text + errorMessage,
          })

          messages.value = messagesCopy
        }
      )
    }

    onMounted(async () => {
      const companyDetails: any = await getCompanyDetails()
      signedAiDisclaimer.value = companyDetails.ai_features_enabled
    })

    return {
      messages,
      avatar,
      chatConfig,
      isLoading,
      handleSubmit,
      signedAiDisclaimer,
    }
  },
}
